import {colorToRGBArray} from '../helpers/helpers';
import LinearDataTexture from './linear-data-texture';

export default class {

    constructor(parameters) {
        const me = this,
            chunkSize = parameters.chunkSize;

        me.texture = new LinearDataTexture(0, 'ubyte4', chunkSize);
        me.groups = new Map();
        me.groupCount = 0;
        me.freeIndices = [];
        me.lookup = new Map();
    }

    addGroup(data) {
        const me = this,
            {freeIndices, lookup} = me,
            ids = [],
            items = [],
            offset = me.texture.size / 4;
        let count = 0;

        for (const {id, color} of data) {
            let index;

            if (freeIndices.length > 0) {
                index = freeIndices.pop();
            } else {
                index = offset + count;
                count += 1;
            }
            ids.push(id);
            lookup.set(id, {index});
            items.push({index, color});
        }

        me.groups.set(me.groupCount, {ids});

        const texture = new LinearDataTexture(me.texture, count * 4);

        me.texture.dispose();
        me.texture = texture;

        const array = texture.image.data;

        for (const {index, color} of items) {
            const colors = Array.isArray(color) ? color : [color];

            array.set([
                ...colorToRGBArray(colors[0]), 255,
                ...colorToRGBArray(colors[1] || colors[0]), 255,
                ...colorToRGBArray(colors[2] || colors[0]), 255,
                ...colorToRGBArray(colors[3] || '#00ff00'), 255
            ], index * 16);
        }

        texture.needsUpdate = true;

        return me.groupCount++;
    }

    removeGroup(groupIndex) {
        const me = this,
            {groups, freeIndices, lookup} = me,
            ids = groups.get(groupIndex).ids;

        groups.delete(groupIndex);

        for (const id of ids) {
            freeIndices.push(lookup.get(id).index);
            lookup.delete(id);
        }
    }

    /**
     * Repaints an entry that is already in the texture, so instances pointing at it
     * change colour without being rebuilt.
     * @param {string} id - Entry ID
     * @param {string} color - New colour
     */
    setColor(id, color) {
        const me = this,
            {index} = me.lookup.get(id),
            rgb = [...colorToRGBArray(color), 255];

        me.texture.image.data.set([...rgb, ...rgb, ...rgb, ...rgb], index * 16);
        me.texture.needsUpdate = true;
    }

    /**
     * Reads back the four sub-colours of an entry. An entry holds four so the car
     * texture masks can each pick their own (line colour, accent, and so on), and a
     * film laid over the object has to cover all four or the accent stays untinted.
     * @param {number} index - Entry index
     * @returns {Array<Array<number>>} Four RGB triples, each channel 0-255
     */
    getColorsByIndex(index) {
        const data = this.texture.image.data,
            colors = [];

        for (let i = 0; i < 4; i++) {
            const offset = index * 16 + i * 4;

            colors.push([data[offset], data[offset + 1], data[offset + 2]]);
        }

        return colors;
    }

    /**
     * Writes the four sub-colours of an entry. Alpha stays 255: the shader reads this
     * texture as opaque, so translucency has to be baked into the RGB by the caller.
     * @param {number} index - Entry index
     * @param {Array<Array<number>>} colors - Four RGB triples, each channel 0-255
     */
    setColorsByIndex(index, colors) {
        const me = this,
            data = [];

        for (let i = 0; i < 4; i++) {
            data.push(...colors[i], 255);
        }
        me.texture.image.data.set(data, index * 16);
        me.texture.needsUpdate = true;
    }

    getIndex(id) {
        return this.lookup.get(id).index;
    }

    /**
     * True if `id` has an entry. getIndex throws on a miss, and a throw inside the
     * animation loop stops every object from being drawn, so callers that build an
     * id from data (GTFS route ids) have to be able to check first.
     * @param {string} id - Entry ID
     * @returns {boolean} True if the entry exists
     */
    has(id) {
        return this.lookup.has(id);
    }

    dispose() {
        this.texture.dispose();
    }

}
